'use client';

import { Suspense, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { motion } from 'framer-motion';
import Loading from '@/components/Loading';

interface ServiceLayoutProps {
    children: React.ReactNode;
    showFooter?: boolean;
}

export default function ServiceLayout({ children, showFooter = true }: ServiceLayoutProps) {
    const contentRef = useRef<HTMLDivElement>(null);

    const ServiceFooter = dynamic(() => import('@/components/layout/service/ServiceFooter'), {
        ssr: false,
    });

    useEffect(() => {
        const element = contentRef.current;
        if (!element) return;

        const preventScroll = (e: TouchEvent) => {
            e.stopPropagation();
        };

        element.addEventListener('touchstart', preventScroll, { passive: true });
        element.addEventListener('touchmove', preventScroll, { passive: true });

        return () => {
            element.removeEventListener('touchstart', preventScroll);
            element.removeEventListener('touchmove', preventScroll);
        };
    }, []);

    return (
        <div className="flex flex-col justify-start items-start w-full">
            <div
                className={`flex flex-col flex-shrink-0 justify-start items-start bg-color-light-gray min-w-full min-h-full ${
                    showFooter ? 'md:pb-[12px]' : ''
                }`}
            >
                <div
                    ref={contentRef}
                    className={`flex flex-col justify-start items-center rounded-md w-full bg-color-white-base/80 shadow-right-bottom ${
                        showFooter ? 'min-h-[133vh]' : 'min-h-[calc(100vh-85px)] h-[calc(100vh-85px)]'
                    }`}
                    style={{
                        touchAction: 'pan-y',
                        WebkitOverflowScrolling: 'auto',
                    }}
                >
                    <Suspense fallback={<Loading />}>
                        <div className="flex flex-col flex-1 w-full">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, delay: 0.5 }}
                                className="flex-1 w-full"
                            >
                                {children}
                            </motion.div>
                            {showFooter && (
                                <div className="w-full">
                                    <ServiceFooter />
                                </div>
                            )}
                        </div>
                    </Suspense>
                </div>
            </div>
        </div>
    );
}
